export class WeglotService {
  private isInitialized: boolean = false
  private initializationPromise: Promise<void> | null = null
  private initialLanguage: string | null = null

  constructor(private weglotApiKey: string) {
    this.init()
  }

  private async init() {
    this.initializationPromise = this.initializeWeglot()
    
    try {
      await this.initializationPromise
      this.isInitialized = true
      this.initialLanguage = this.getCurrentLanguage()
      
      this.fixBilingualLinks()
      this.setupCustomLanguageSelectors()
      this.setupLanguageChangeListener()
      
      window.dispatchEvent(new CustomEvent('weglotReady'))
    } catch (error) {
      console.error('Weglot initialization failed:', error)
    }
  }

  private async initializeWeglot(): Promise<void> {
    await this.loadWeglotScript()
    
    // @ts-ignore
    if (typeof Weglot === 'undefined' || !Weglot.initialize) {
      throw new Error('Weglot is not available')
    }
    
    // @ts-ignore
    Weglot.initialize({ api_key: this.weglotApiKey })
    
    await this.waitForWeglotReady()
  }

  private loadWeglotScript(): Promise<void> {
    return new Promise((resolve, reject) => {
      // @ts-ignore
      if (typeof Weglot !== 'undefined') {
        resolve()
        return
      }

      const existingScript = document.querySelector('script[src*="weglot"]')
      if (existingScript) {
        existingScript.addEventListener('load', () => resolve())
        existingScript.addEventListener('error', () => reject(new Error('Script load failed')))
        return
      }

      const script = document.createElement('script')
      script.type = 'text/javascript'
      script.src = 'https://cdn.weglot.com/weglot.min.js'
      script.async = true
      script.onload = () => resolve()
      script.onerror = () => reject(new Error('Script load failed'))
      
      document.head.appendChild(script)
    })
  }

  private waitForWeglotReady(timeout: number = 10000): Promise<void> {
    return new Promise((resolve, reject) => {
      const startTime = Date.now()
      
      const check = () => {
        try {
          // @ts-ignore
          const lang = Weglot?.getCurrentLang?.()
          if (lang !== undefined && lang !== null) {
            resolve()
            return
          }
        } catch (error) {
          // Not ready yet
        }
        
        if (Date.now() - startTime > timeout) {
          reject(new Error('Initialization timeout'))
          return
        }
        
        setTimeout(check, 50)
      }
      
      check()
    })
  }

  private setupLanguageChangeListener() {
    try {
      // @ts-ignore
      Weglot?.on?.("languageChanged", (newLang: string, prevLang: string) => {
        this.fixBilingualLinks()
        
        window.dispatchEvent(new CustomEvent('weglotLanguageChanged', {
          detail: { newLang, prevLang }
        }))
        
        // Only reload if language actually changed (prevents loops on page load)
        if (newLang !== this.initialLanguage) {
          window.location.reload()
        }
      })
    } catch (error) {
      console.error('Failed to setup language change listener:', error)
    }
  }

  private setupCustomLanguageSelectors(): void {
    const languageSelectors = document.querySelectorAll('.language-selector')
    
    languageSelectors.forEach((selector) => {
      selector.addEventListener('click', (event) => {
        event.preventDefault()
        
        const lang = selector.getAttribute('lang')?.toLowerCase()
        const href = selector.getAttribute('href')
        
        if (!lang) {
          console.warn('Language selector missing "lang" attribute')
          return
        }
        
        // Custom selectors are used on pages where Weglot is disabled
        // Set localStorage directly (same key Weglot uses)
        localStorage.setItem('wglang', lang)
        
        // Navigate to href or reload
        if (href && href !== '#') {
          window.location.href = href
        } else {
          window.location.reload()
        }
      })
    })
  }

  private fixBilingualLinks(): void {
    const isEnglish = this.getCurrentLanguage() === 'en'
    const bilingualElements = document.querySelectorAll('.is-billingual')
    
    bilingualElements.forEach((element) => {
      const slugAttr = isEnglish ? 'article-en-slug' : 'article-slug'
      const pathPrefix = isEnglish ? '/article-en/' : '/article/'
      const slug = element.getAttribute(slugAttr)
      
      if (slug) {
        element.setAttribute('href', `${window.location.origin}${pathPrefix}${slug}`)
      }
    })
  }

  /**
   * Returns a promise that resolves when Weglot is initialized
   */
  async whenReady(): Promise<void> {
    if (this.isInitialized) {
      return Promise.resolve()
    }
    if (this.initializationPromise) {
      return this.initializationPromise
    }
    return Promise.reject(new Error('Weglot initialization not started'))
  }

  /**
   * Check if Weglot is initialized
   */
  isReady(): boolean {
    return this.isInitialized
  }

  /**
   * Get current language from Weglot
   */
  getCurrentLanguage(): string | null {
    try {
      // @ts-ignore
      return localStorage.getItem('wglang')
    } catch (error) {
      return error as string ?? null
    }
  }

  /**
   * Switch to a specific language
   */
  switchTo(languageCode: string): void {
    try {
      // @ts-ignore
      Weglot?.switchTo?.(languageCode)
    } catch (error) {
      console.error('Failed to switch language:', error)
    }
  }

  /**
   * Manually refresh custom language selectors
   * Useful if language selectors are added dynamically to the page
   */
  refreshLanguageSelectors(): void {
    if (!this.isInitialized) {
      console.warn('Weglot not initialized yet')
      return
    }
    this.setupCustomLanguageSelectors()
  }
}
